import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { StripeElementLocale } from '@stripe/stripe-js';
import { useRouter } from 'next/router';
import { FormEvent, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TypePaymentOption } from '../../../../@types/generated';
import { FlowType, PaymentType, SubscriptionFlowType } from '../../../types/PaymentTypes';
import {
  ChangeType,
  EVENT_NAMES,
  sendEvent,
  sendSupportDonationChangeEvent,
  trackInitiatePayment,
} from '../../../utils/analytics';
import {
  Currency,
  StripePaymentProvider,
  getPaymentProviders,
} from '../../../utils/api/paymentRequests';
import CheckoutContext from '../../../utils/checkoutContext';
import { microcopySet, useMicrocopy } from '../../../utils/contentfulMicrocopy';
import { formatAmountAndWithDerivedLocale } from '../../../utils/helpers/currency';
import { useAmountSuggestions } from '../../../utils/hooks/useAmountSuggestions';
import { useCurrencies } from '../../../utils/hooks/useCurrencies';
import usePathname from '../../../utils/hooks/usePathname';
import { breakpoints, colors, spacing } from '../../../utils/styleguide';
import { sendGTMEvent } from '../../../utils/tracking/gtm/sendGTMEvent';
import { PaymentFlowTrackingContext } from '../../contentful-elements/contexts/PaymentFlowTrackingContext';
import PrimaryButton from '../buttons/PrimaryButton';
import Switch, { Option } from '../switch/Switch';
import Typography from '../text/Typography';
import PropsComponent from '../types/PropsComponent';
import AmountSelector from './AmountSelector';
import { StripePaymentCategory } from './PaymentCategory';
import PaymentFooter from './PaymentFooter';
import StripeCheckout, { DonationButtonType } from './StripeCheckout';

const CheckoutContainer = styled.div<{ withBorders: boolean; noOuterBorder: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};
  border-radius: ${({ withBorders }) => (withBorders ? '4px 4px 0 0' : '0')};
  border: ${({ noOuterBorder }) => (noOuterBorder ? 'none' : `1px solid ${colors.gray}`)};
  overflow: hidden;
  width: ${({ withBorders }) => (withBorders ? 'auto' : '100%')};
`;

const AmountSelectorContainer = styled.form<{ withBorders: boolean; colorTheme: string }>`
  display: flex;
  flex-direction: column;
  padding: ${spacing[4]}px ${spacing[3]}px;

  ${breakpoints.desktop} {
    padding: ${spacing[5]}px ${spacing[4]}px;
  }
`;

const StripeCheckoutContainer = styled.div<{ show?: boolean; withBorders: boolean }>`
  display: ${(props) => (props.show ? 'block' : 'none')};
  padding: ${spacing[4]}px ${spacing[3]}px;

  ${breakpoints.desktop} {
    padding: ${spacing[5]}px ${spacing[4]}px;
  }
`;

const InLineTextButton = styled.span`
  color: ${colors.blueDark};
  cursor: pointer;
`;

type Props = {
  amountCustomLabels?: ReactNode[];
  titleSection?: ReactNode;
  DonationButton?: DonationButtonType;
  DonationHeader?: PropsComponent<{
    formattedAmount: string;
    toggleShowPaymentOptions: () => void;
  }>;
  DonationFooter?: PropsComponent<{
    donationClarification?: string;
    securePaymentsLabel?: string;
    termsAndPrivacyCopyright: Record<string, string>;
  }>;
  DonationCheckoutButton?: DonationButtonType;
  id: string;
  onSuccess?: (obj: { subscriptionId?: string; paymentRequestId?: string }) => void;
  campaign?: string;
  paymentOption?: TypePaymentOption['fields'];
  projectId?: number;
  category?: StripePaymentCategory;
  locale?: StripeElementLocale;
  variant?: 'with-borders' | 'without-borders';
  noOuterBorder?: boolean;
  colorTheme?: 'default' | 'dark' | 'ppc';
};

const StripeCheckoutWithSelector = ({
  amountCustomLabels,
  titleSection,
  DonationButton,
  DonationHeader,
  DonationFooter,
  DonationCheckoutButton,
  id,
  onSuccess,
  campaign,
  paymentOption,
  projectId,
  category,
  locale,
  colorTheme = 'default',
  variant = 'with-borders',
  noOuterBorder = false,
}: Props) => {
  const t = useMicrocopy();
  const router = useRouter();
  const { initiator, grantmakerName } = useContext(CheckoutContext);
  const { paymentFlowId, contentfulLandingPageId } = useContext(PaymentFlowTrackingContext);
  const [amount, setAmount] = useState<number>();
  const [showPaymentOptions, setShowPaymentOptions] = useState<boolean>(false);
  const [isCustomAmount, setIsCustomAmount] = useState(false);
  const [option, setOption] = useState<StripePaymentProvider>();
  const [currency, setCurrency] = useState<Currency>();
  const path = usePathname();
  const initialPaymentType =
    paymentOption?.donationType?.length === 1
      ? paymentOption?.donationType[0]
      : paymentOption?.defaultDonationType || PaymentType.SUBSCRIPTION;
  const [paymentType, setPaymentType] = useState<PaymentType>(initialPaymentType as PaymentType);

  const withBorders = variant === 'with-borders';
  const {
    currencies,
    defaultCurrencyCode,
    isLoading: isLoadingCurrencies,
  } = useCurrencies('stripe');
  const { amountSuggestions, defaultAmountSuggestion, isLoadingAmountSuggestions } =
    useAmountSuggestions({
      currency,
      amountConfiguration:
        paymentType === PaymentType.SUBSCRIPTION
          ? paymentOption?.subscriptionAmountConfiguration
          : paymentOption?.oneTimeAmountConfiguration,
    });

  useEffect(() => {
    if (!defaultAmountSuggestion || !currency) return;

    // Should be send only once after component rendered with amount and currency data
    sendGTMEvent('productView', {
      currencyCode: currency?.code,
      amount: defaultAmountSuggestion,
      paymentType,
      items: category ? [{ id: category.id, name: category.name }] : [],
      donationItemType: category ? 'Category' : undefined,
    });
  }, [currency, defaultAmountSuggestion]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (defaultAmountSuggestion) setAmount(defaultAmountSuggestion);
  }, [amountSuggestions, defaultAmountSuggestion]);

  useEffect(() => {
    let isComponentRendered = true;
    const fetchData = async () => {
      if (isComponentRendered) setOption(undefined);

      if (currency?.code) {
        const options = await getPaymentProviders({
          currencyCode: currency.code,
          grantmakerName,
          categoryId: category?.id,
        });
        if (isComponentRendered) {
          // The configuration type returned from the API is  "... || { [key: string]: (boolean & true) | undefined }"
          // which makes it very difficult to typeguard against. The following casting is a workaround
          setOption(
            options.result.find((option) => option.name === 'stripe') as
              | StripePaymentProvider
              | undefined,
          );
        }
      }
    };

    fetchData();

    return () => {
      isComponentRendered = false;
    };
  }, [currency, grantmakerName, category?.id]);

  useEffect(() => {
    setCurrency(currencies?.find((currency) => currency.code === defaultCurrencyCode));
  }, [currencies, defaultCurrencyCode]);

  useEffect(() => {
    trackInitiatePayment({
      flowType: paymentType === PaymentType.ONE_TIME ? FlowType.ONE_TIME : FlowType.SUBSCRIPTION,
      initiator: initiator,
      campaign,
      categoryId: category?.id,
      categoryName: category?.name,
      paymentFlowId,
      provider: 'stripe',
    });
  }, [initiator, category, paymentFlowId, campaign, paymentType]);

  const handleSelectCurrency = useCallback(
    (currencyCode: string) => {
      sendSupportDonationChangeEvent({
        amount,
        currencyCode: currencyCode,
        paymentProvider: option?.name,
        supportType: paymentType === PaymentType.ONE_TIME ? 'one time' : 'subscription',
        path,
        changeType: ChangeType.currency,
        flowType: SubscriptionFlowType.createSubscription,
      });
      if (currencies) {
        setCurrency(currencies.find((currency) => currency.code === currencyCode));
      }
    },
    [amount, currencies, option?.name, paymentType, path],
  );

  const handleOnAmountChange = useCallback(
    ({ amount, isCustomAmount }: { amount: number; isCustomAmount: boolean }) => {
      sendSupportDonationChangeEvent({
        amount,
        currencyCode: currency?.code,
        paymentProvider: option?.name,
        supportType: paymentType === PaymentType.ONE_TIME ? 'one time' : 'subscription',
        path,
        changeType: ChangeType.amount,
        flowType: SubscriptionFlowType.createSubscription,
      });
      setAmount(amount);
      setIsCustomAmount(isCustomAmount);
    },
    [currency?.code, option?.name, paymentType, path],
  );

  /**
   * NOTE: this feature is added to help our dear colleagues out with testing the complete
   * payment flow without having to spend the otherwise minimum amount of ~50SEK each time.
   */
  const testAmount = Number(router.query && router.query['test-custom-amount']);
  const isTestingPayment = Number.isInteger(testAmount);
  const testPaymentAmountOptions = [testAmount, ...(amountSuggestions || [])];
  const amountOptions = isTestingPayment ? testPaymentAmountOptions : amountSuggestions;
  const paymentOptions: [Option, Option] = [
    {
      label: t(microcopySet.PAYMENT_OPTION, 'giveOnce'),
      value: PaymentType.ONE_TIME,
    },
    {
      label: t(microcopySet.PAYMENT_OPTION, 'monthly'),
      value: PaymentType.SUBSCRIPTION,
    },
  ];

  const title = paymentOption?.title ? paymentOption?.title : 'Choose an amount';
  const donationClarification = paymentOption?.donationClarification;

  const buttonLabel = t(microcopySet.PAYMENT_OPTION, 'donate');
  const securePaymentsLabel = t(microcopySet.PAYMENT_OPTION, 'securePayments');
  const termsAndPrivacyCopyright = useMemo(
    () => ({
      donationTerms: t(microcopySet.PAYMENT_OPTION, 'donationTerms'),
      terms: t(microcopySet.PAYMENT_OPTION, 'checkoutTerms'),
      and: t(microcopySet.PAYMENT_OPTION, 'checkoutAnd'),
      privacy: t(microcopySet.PAYMENT_OPTION, 'checkoutPrivacy'),
    }),
    [t],
  );
  const customAmountLabel = paymentOption?.skipCustomAmount
    ? null
    : t(microcopySet.PAYMENT_OPTION, 'otherAmount');
  const editAmountLabel = '(' + t(microcopySet.PAYMENT_OPTION, 'editAmount') + ')';
  const enableDonate = Boolean(currency && option && amount);
  const showHeader = showPaymentOptions && currency;

  const handleSetPaymentOption = useCallback(
    (newPaymentType: string) => {
      sendSupportDonationChangeEvent({
        amount,
        currencyCode: currency?.code,
        paymentProvider: option?.name,
        supportType: paymentType === PaymentType.ONE_TIME ? 'one time' : 'monthly',
        path,
        changeType: ChangeType.toggle,
        flowType: SubscriptionFlowType.createSubscription,
      });
      setPaymentType(newPaymentType as PaymentType);
    },
    [amount, currency?.code, option?.name, paymentType, path],
  );

  const handleProceedToPaymentMethod = useCallback(
    (event?: FormEvent) => {
      event?.preventDefault();
      sendEvent({
        name: EVENT_NAMES.supportProceedToPaymentMethodSelection,
        properties: {
          amount,
          'currency code': currency?.code,
          'payment provider': option?.name,
          'support type': paymentType === PaymentType.ONE_TIME ? 'one time' : 'monthly',
          path,
          'flow type': SubscriptionFlowType.createSubscription,
        },
      });

      sendGTMEvent('addToCart', {
        currencyCode: currency?.code,
        amount,
        paymentType: paymentType,
        items: category ? [category] : [],
        donationItemType: category ? 'Category' : undefined,
      });

      setShowPaymentOptions(true);
    },
    [amount, category, currency?.code, option?.name, paymentType, path],
  );

  return (
    <>
      <CheckoutContainer id={id} withBorders={withBorders} noOuterBorder={noOuterBorder}>
        {!showPaymentOptions && (
          <AmountSelectorContainer
            withBorders={withBorders}
            colorTheme={colorTheme}
            onSubmit={handleProceedToPaymentMethod}
          >
            {titleSection || (
              <Typography
                variant="bodyBold"
                css={css`
                  margin-bottom: ${spacing[5]}px;
                `}
              >
                {title}
              </Typography>
            )}

            {(!paymentOption?.donationType || paymentOption?.donationType.length === 2) && (
              <Switch
                value={paymentType}
                onChange={handleSetPaymentOption}
                options={paymentOptions}
                css={css`
                  margin-bottom: ${paymentType === PaymentType.SUBSCRIPTION
                    ? `${spacing[1]}px`
                    : `${spacing[6]}px`};
                `}
              />
            )}

            {paymentType === PaymentType.SUBSCRIPTION && (
              <Typography
                css={css`
                  margin-bottom: ${spacing[3]}px;
                `}
                variant="bodySmall"
                color={colors.blackSecondary}
              >
                {t(microcopySet.PAYMENT_OPTION, 'cancelAnytime')}
              </Typography>
            )}

            <AmountSelector
              amountCustomLabels={amountCustomLabels}
              amountOptions={amountOptions}
              customAmountLabel={customAmountLabel}
              amount={amount}
              isCustomAmount={isCustomAmount}
              onAmountChange={handleOnAmountChange}
              currency={currency}
              css={css`
                width: 100%;
              `}
              showSkeleton={isLoadingAmountSuggestions}
              type={paymentType === PaymentType.ONE_TIME ? 'one time' : 'monthly'}
              colorTheme={colorTheme}
              currencies={currencies}
              handleSelectCurrency={handleSelectCurrency}
              isLoadingCurrencies={isLoadingCurrencies}
            />

            {DonationButton ? (
              <DonationButton
                type="submit"
                label={buttonLabel}
                disabled={!enableDonate}
                onClick={handleProceedToPaymentMethod}
              />
            ) : (
              <PrimaryButton
                type="submit"
                variant="solid"
                label={buttonLabel}
                disabled={!enableDonate}
                onClick={handleProceedToPaymentMethod}
                css={css`
                  margin: ${spacing[5]}px 0px ${spacing[4]}px;
                  width: 100%;
                  ${colorTheme === 'dark' &&
                  css`
                    color: ${colors.white};
                    background-color: ${colors.black};
                    border-color: ${colors.black};
                    ${enableDonate &&
                    css`
                      :hover {
                        color: ${colors.white};
                        background-color: ${colors.black};
                        border-color: ${colors.black};
                      }
                    `}
                  `}
                `}
              />
            )}
          </AmountSelectorContainer>
        )}

        <StripeCheckoutContainer
          show={Boolean(currency && option && showPaymentOptions)}
          withBorders={withBorders}
        >
          {showHeader && DonationHeader && (
            <DonationHeader
              formattedAmount={
                amount ? formatAmountAndWithDerivedLocale(amount, currency?.code) : ''
              }
              toggleShowPaymentOptions={() => setShowPaymentOptions(false)}
            />
          )}
          {showHeader && !DonationHeader && (
            <Typography
              variant="bodyBold"
              css={css`
                margin-bottom: ${spacing[4]}px;
              `}
            >
              {` ${t(microcopySet.PAYMENT_OPTION, 'giving')} ${
                amount ? formatAmountAndWithDerivedLocale(amount, currency?.code) : ''
              } ${
                paymentType === PaymentType.SUBSCRIPTION
                  ? ` ${t(microcopySet.PAYMENT_OPTION, 'monthly').toLowerCase()}`
                  : ''
              } `}
              <InLineTextButton onClick={() => setShowPaymentOptions(false)}>
                {editAmountLabel}
              </InLineTextButton>
            </Typography>
          )}
          {currency && option && (
            <StripeCheckout
              amount={amount}
              buttonLabel={buttonLabel}
              campaign={campaign}
              category={category}
              colorTheme={colorTheme}
              contentfulLandingPageId={contentfulLandingPageId}
              currencyCode={currency.code}
              enableStripePaymentRequestButton={paymentOption?.enableStripePaymentRequestButton}
              initiator={initiator}
              isVisible={Boolean(currency && option && showPaymentOptions)}
              locale={locale}
              onSuccess={onSuccess}
              paymentFlowId={paymentFlowId}
              paymentType={paymentType}
              projectId={projectId}
              publicKey={option.configuration.publishableKey}
              stripeAccountId={option.configuration.stripeAccountId}
              grantmakerId={option.grantmakerId}
              skipAmountValidation={isTestingPayment}
              variant="dense"
              DonationCheckoutButton={DonationCheckoutButton}
            />
          )}
        </StripeCheckoutContainer>
      </CheckoutContainer>
      {DonationFooter ? (
        <DonationFooter
          donationClarification={donationClarification}
          securePaymentsLabel={securePaymentsLabel}
          termsAndPrivacyCopyright={termsAndPrivacyCopyright}
        />
      ) : (
        <PaymentFooter
          donationClarification={donationClarification}
          securePaymentsLabel={securePaymentsLabel}
          termsAndPrivacyCopyright={termsAndPrivacyCopyright}
        />
      )}
    </>
  );
};

export default StripeCheckoutWithSelector;
